import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ChevronDown } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';

export function Topbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  if (!user) return null;

  const initials = user.fullName
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-brand-gray-light bg-white px-5">
      <Logo height={32} />

      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className={cn(
            'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-brand-charcoal',
            'hover:bg-brand-off-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue',
          )}
        >
          <span
            className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-red text-xs font-semibold text-white"
            aria-hidden
          >
            {initials || '?'}
          </span>
          <span className="hidden text-left leading-tight md:block">
            <span className="block text-sm font-medium">{user.fullName}</span>
            <span className="block text-xs text-brand-gray-mid">{user.role}</span>
          </span>
          <ChevronDown className="h-4 w-4 text-brand-gray-mid" aria-hidden />
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-md border border-brand-gray-light bg-white shadow-card-hover"
          >
            <div className="border-b border-brand-gray-light px-4 py-3">
              <p className="text-sm font-medium text-brand-charcoal">{user.fullName}</p>
              <p className="text-xs text-brand-gray-mid">@{user.username}</p>
              <p className="mt-1 inline-block rounded-sm bg-brand-red/10 px-1.5 py-0.5 text-xs font-medium text-brand-red">
                {user.role}
              </p>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-brand-charcoal hover:bg-brand-off-white"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span>Sign out</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
